import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { Modal } from '@/common/components/Modal/Modal';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { LANG } from '@/common/constants/language';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { usePostPromoCode } from '@/common/integrations/menhir/employee/discounts/discounts.hooks';
import { IDiscount } from '@/common/integrations/menhir/employee/discounts/discounts.types';
import { copyToClipboard } from '@/common/utils/copy';
import { isAmountDiscount } from '@/common/utils/discounts';
import { isPercentageDiscount } from '@/common/utils/discounts';
import { notify } from '@/common/utils/toast/toast';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { Image } from 'expo-image';
import { styles } from './ModalDiscount.styles';

export type TModalDiscountProps = {
  discount: IDiscount;
  onClose: () => void;
};

export function ModalDiscount({ discount, onClose }: TModalDiscountProps) {
  const { t } = useTranslation();
  const { employee } = useEmployeeDetails();
  const [generatedCode, setGeneratedCode] = useState<string>('');

  const { mutateAsync, isLoading } = usePostPromoCode();

  const handleCopyClick = () => {
    AnalyticsService.trackCustomEvent(
      'discounts_copy_promocode_button_clicked',
      {
        category: discount.category,
        partner: discount.partnerName,
      }
    );
    copyToClipboard(
      discount.promoCode ? discount.promoCode : generatedCode || ''
    );
    notify({
      message: t('wallet.discounts.promocode.clipboard_feedback'),
      type: 'success',
    });
  };

  const handleGeneratecode = async () => {
    try {
      const discountCode = await mutateAsync({ discount_id: discount.id });
      setGeneratedCode(discountCode.promoCode);
    } catch (error) {
      notify({
        type: 'error',
        message: t('wallet.discounts.generate_code_error'),
      });
    } finally {
    }
  };

  const learnMoreLink = useMemo(() => {
    if (!employee.language || !discount.conditionLinks) {
      return undefined;
    }

    return (
      discount.conditionLinks[employee.language] ??
      discount.conditionLinks[LANG.EN]
    );
  }, [employee, discount]);

  const discountValue = isPercentageDiscount(discount)
    ? `${discount.percentage}%`
    : isAmountDiscount(discount)
      ? MoneyUtils.moneyToString(discount.amount, { integer: true })
      : undefined;

  const hasCode = Boolean(
    discount.promoCode || generatedCode || discount.providerInfos
  );

  return (
    <Modal
      onClose={onClose}
      icon={
        <Image
          source={{ uri: discount.logoUrl }}
          alt={discount.partnerName}
          className={styles.icon}
          contentFit="contain"
          transition={250}
        />
      }
      iconBadgeColor="neutral10"
      title={t(`wallet.discounts.${discount.partnerName}.name`)}
      footer={
        hasCode ? (
          <Stack horizontal gap="md" align="center">
            {discount.promoCode || generatedCode ? (
              <>
                <Stack
                  className={styles.codeWrapper}
                  grow
                  align="center"
                  justify="center"
                >
                  <Label bold className={styles.code} numberOfLines={1}>
                    {discount.promoCode ? discount.promoCode : generatedCode}
                  </Label>
                </Stack>
                <Button
                  icon={<Icon icon={['far', 'copy']} />}
                  onPress={handleCopyClick}
                >
                  {t('wallet.discounts.promocode.copy_label')}
                </Button>
              </>
            ) : (
              discount.providerInfos && (
                <Button
                  className={styles.codeBtn}
                  icon={<Icon icon="wand-sparkles" />}
                  onPress={handleGeneratecode}
                  loading={isLoading}
                >
                  {t('wallet.discounts.promocode.generate_code')}
                </Button>
              )
            )}
          </Stack>
        ) : (
          learnMoreLink && (
            <Button
              onPress={() => {
                AnalyticsService.trackCustomEvent(
                  'discounts_learn_more_button_clicked',
                  {
                    category: discount.category,
                    partner: discount.partnerName,
                  }
                );
              }}
              as="a"
              href={learnMoreLink}
              hrefAttrs={{ target: 'blank', rel: 'noreferrer noopener' }}
              variant="secondary"
              icon={<Icon icon="arrow-up-right" />}
              iconAlign="right"
            >
              {t('wallet.discounts.learn_more')}
            </Button>
          )
        )
      }
    >
      <Stack gap="lg" align="start">
        <Translate
          parent={BodyText}
          i18nKey={`wallet.discounts.${discount.partnerName}.description_long`}
          values={{
            discount_value: discountValue,
          }}
        />
        {learnMoreLink && hasCode && (
          <TextAction
            onPress={() => {
              AnalyticsService.trackCustomEvent(
                'discounts_learn_more_button_clicked',
                { category: discount.category, partner: discount.partnerName }
              );
            }}
            href={learnMoreLink}
            hrefAttrs={{ target: 'blank', rel: 'noreferrer noopener' }}
          >
            {t('wallet.discounts.learn_more')}
          </TextAction>
        )}
      </Stack>
    </Modal>
  );
}
